import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface MessageSender {
  id: string;
  nome: string | null;
  foto_url: string | null;
}

/**
 * Cache global de remetentes (consultores) referenciados em mensagens outbound.
 * Faz UMA única query agrupada por lista de userIds e mantém em cache por 10min.
 */
export function useMessageSenders(userIds: (string | null | undefined)[]) {
  const distinct = Array.from(
    new Set(userIds.filter((x): x is string => !!x))
  ).sort();

  const queryKey = ['message-senders', distinct.join(',')];

  const { data } = useQuery({
    queryKey,
    queryFn: async () => {
      if (distinct.length === 0) return new Map<string, MessageSender>();
      const { data, error } = await supabase
        .from('usuarios')
        .select('id, nome, foto_url')
        .in('id', distinct);
      if (error) {
        console.error('[useMessageSenders] error:', error);
        return new Map<string, MessageSender>();
      }
      const map = new Map<string, MessageSender>();
      (data || []).forEach((u) => {
        map.set(u.id, { id: u.id, nome: u.nome, foto_url: u.foto_url });
      });
      return map;
    },
    enabled: distinct.length > 0,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return data ?? new Map<string, MessageSender>();
}

/**
 * Hash determinístico → cor (estilo grupo do WhatsApp).
 * Usa classes Tailwind semânticas próximas (verde/azul/roxo/laranja/rosa/teal).
 */
const SENDER_COLORS = [
  'text-success dark:text-success',
  'text-info dark:text-info',
  'text-violet-600 dark:text-violet-400',
  'text-warning dark:text-warning',
  'text-pink-600 dark:text-pink-400',
  'text-teal-600 dark:text-teal-400',
  'text-warning dark:text-warning',
  'text-fuchsia-600 dark:text-fuchsia-400',
];

export function getSenderColorClass(userId: string | null | undefined): string {
  if (!userId) return 'text-muted-foreground';
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) >>> 0;
  }
  return SENDER_COLORS[hash % SENDER_COLORS.length];
}

export function getSenderInitials(nome: string | null | undefined): string {
  if (!nome) return '?';
  return nome.split(' ').filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase();
}
